/**
 * MobileEvaluationsList
 *
 * Mobile card-based replacement for EvaluationsTable.
 * Shows each collaborator as a touch-friendly card with a month grid
 * displaying evaluation status via colored circles.
 * Supports search, team grouping with collapse, and tap-to-edit per month.
 */
import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  Fade,
  Collapse,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import moment from "moment";
import Langs, { Localize } from "../../../app/lang/langs";
import { getRatioColor } from "../utils/evaluationUtils";

/* ------------------------------------------------------------------ */
/*  Style constants (matching MobileContractsList / MobileCollabsList) */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const EASE_OUT = "cubic-bezier(0, 0, 0.2, 1)";
const GLASS_BG = "rgba(255,255,255,0.72)";
const GLASS_BG_CARD = "rgba(255,255,255,0.82)";
const GLASS_BORDER = "1px solid rgba(255,255,255,0.45)";
const SHADOW_CARD =
  "0 2px 12px rgba(0,40,128,0.06), 0 1px 4px rgba(0,0,0,0.04)";
const SHADOW_CARD_ACTIVE = "0 1px 4px rgba(0,40,128,0.04)";
const SHADOW_SEARCH_FOCUS = "0 0 0 3px rgba(255,144,21,0.18)";
const SHADOW_AVATAR_INTERNE =
  "0 2px 8px rgba(255,144,21,0.35), 0 0 0 2px rgba(255,255,255,0.8)";
const BADGE_BG = "rgba(255,144,21,0.1)";
const BADGE_COLOR = "rgb(255,144,21)";

/* ------------------------------------------------------------------ */
/*  Month abbreviations                                                */
/* ------------------------------------------------------------------ */
const MONTH_ABBR = [
  "Jan", "Fév", "Mar", "Avr", "Mai", "Jui",
  "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc",
];

/**
 * @param {Object}   props
 * @param {Array}    props.groupedData     - array of { team_name, id_team, rows: [...] }
 * @param {Array}    props.visibleTeams    - array of visible team IDs
 * @param {number}   props.expandedTeamId  - currently expanded team ID
 * @param {Function} props.onToggleGroup   - toggle team expansion
 * @param {number}   props.year            - current evaluation year
 * @param {Function} props.onClickCell     - called when tapping a month circle
 */
export default function MobileEvaluationsList({
  groupedData,
  visibleTeams,
  expandedTeamId,
  onToggleGroup,
  year,
  onClickCell,
}) {
  const [searchText, setSearchText] = useState("");

  // Flatten all rows with team info for search filtering
  const allRows = useMemo(() => {
    if (!groupedData) return [];
    return groupedData.flatMap((group) =>
      (group.rows || []).map((row) => ({
        ...row,
        team_name: group.team_name,
      }))
    );
  }, [groupedData]);

  // Filtered grouped data based on search
  const filteredGroupedData = useMemo(() => {
    if (!groupedData) return [];
    if (!searchText.trim()) return groupedData;

    const terms = searchText.toLowerCase().split(" ").filter(Boolean);

    return groupedData
      .map((group) => ({
        ...group,
        rows: (group.rows || []).filter((row) => {
          const searchStr = [
            row.nom,
            row.prenom,
            row.matricule,
            group.team_name,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return terms.every((term) => searchStr.includes(term));
        }),
      }))
      .filter((group) => group.rows.length > 0);
  }, [groupedData, searchText]);

  // Total filtered count
  const totalCount = useMemo(
    () => filteredGroupedData.reduce((sum, g) => sum + g.rows.length, 0),
    [filteredGroupedData]
  );

  // Handle month circle tap
  const handleMonthTap = (row, monthNumber) => {
    const collab = {
      id: row.id_collab,
      nom: row.nom,
      prenom: row.prenom,
      id_team: row.id_team,
    };
    const date = moment({ year, month: monthNumber - 1, day: 1 }).endOf("month");
    const dateStartMonth = moment({ year, month: monthNumber - 1, day: 1 }).startOf("month");
    onClickCell(collab, date, row.entree_date, row.sortie_date, row.id_team, dateStartMonth);
  };

  // Check if a month is in the future
  const isFutureMonth = (monthNumber) => {
    const date = moment({ year, month: monthNumber - 1, day: 1 }).endOf("month");
    return date.isAfter(moment().endOf("month"));
  };

  return (
    <Box
      sx={{
        px: 2,
        pt: 1.5,
        pb: 2.5,
        mt: 0,
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Search bar — glassmorphic */}
      <TextField
        fullWidth
        size="small"
        placeholder={Localize("Search") + "..."}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                fontSize="small"
                sx={{
                  color: "rgba(0,40,128,0.4)",
                  transition: TRANSITION_SMOOTH,
                }}
              />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 1.5,
          "& input": {
            fontSize: "16px",
            fontWeight: 400,
            letterSpacing: "0.01em",
            "&::placeholder": { opacity: 0.45 },
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: "14px",
            bgcolor: GLASS_BG,
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            border: GLASS_BORDER,
            transition: TRANSITION_SMOOTH,
            boxShadow: "0 2px 8px rgba(0,40,128,0.04)",
            "& fieldset": {
              borderColor: "transparent",
              transition: TRANSITION_SMOOTH,
            },
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.82)",
              boxShadow: "0 2px 12px rgba(0,40,128,0.07)",
              "& fieldset": { borderColor: "rgba(0,40,128,0.1)" },
            },
            "&.Mui-focused": {
              bgcolor: "rgba(255,255,255,0.9)",
              boxShadow: SHADOW_SEARCH_FOCUS,
              "& fieldset": {
                borderColor: "rgb(255,144,21) !important",
                borderWidth: "1.5px !important",
              },
              "& .MuiInputAdornment-root svg": {
                color: "rgb(255,144,21)",
              },
            },
          },
        }}
      />

      {/* Results count — pill badge */}
      <Box sx={{ mb: 1.25, px: 0.25 }}>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1.25,
            py: 0.35,
            borderRadius: "10px",
            bgcolor: BADGE_BG,
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: "0.72rem",
              letterSpacing: "0.03em",
              color: BADGE_COLOR,
              lineHeight: 1.5,
            }}
          >
            {totalCount}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              fontSize: "0.72rem",
              letterSpacing: "0.02em",
              color: "rgba(0,40,128,0.55)",
              lineHeight: 1.5,
            }}
          >
            {Localize("results")}
          </Typography>
        </Box>
      </Box>

      {/* Scrollable card list */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          pb: 2,
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { width: 5 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0,40,128,0.1)",
            borderRadius: 3,
            "&:hover": { background: "rgba(0,40,128,0.18)" },
          },
        }}
      >
        {filteredGroupedData.map((group) => {
          const isExpanded =
            expandedTeamId === group.id_team || visibleTeams.length === 1;

          return (
            <Box key={group.id_team} sx={{ mb: 1.5 }}>
              {/* Team group header */}
              {visibleTeams.length > 1 && (
                <Box
                  onClick={() => onToggleGroup(group.id_team)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1.5,
                    py: 1,
                    mb: 1,
                    borderRadius: "14px",
                    bgcolor: "rgba(255,144,21,0.06)",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    border: "1px solid rgba(255,144,21,0.12)",
                    cursor: "pointer",
                    transition: TRANSITION_SMOOTH,
                    "&:active": {
                      transform: "scale(0.98)",
                      bgcolor: "rgba(255,144,21,0.1)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        color: "rgba(0,40,128,0.85)",
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {group.team_name}
                    </Typography>
                    <Chip
                      label={group.rows.length}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        borderRadius: "8px",
                        bgcolor: "rgba(255,144,21,0.12)",
                        color: BADGE_COLOR,
                      }}
                    />
                  </Box>
                  <IconButton
                    size="small"
                    sx={{
                      transition: TRANSITION_SMOOTH,
                      color: "rgba(0,40,128,0.5)",
                    }}
                  >
                    {isExpanded ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              )}

              {/* Collapsible rows */}
              <Collapse in={isExpanded} timeout={300}>
                {group.rows.map((row, index) => (
                  <Fade
                    in
                    key={row.id_collab}
                    timeout={300 + index * 40}
                    easing={{ enter: EASE_OUT }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        mb: 1.5,
                        borderRadius: "18px",
                        overflow: "hidden",
                        bgcolor: GLASS_BG_CARD,
                        backdropFilter: "blur(20px)",
                        WebkitBackdropFilter: "blur(20px)",
                        border: GLASS_BORDER,
                        boxShadow: SHADOW_CARD,
                        transition: `transform 280ms ${EASE_OUT}, box-shadow 280ms ${EASE_OUT}`,
                      }}
                    >
                      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                        {/* Top row: avatar + name + team chip */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.25,
                              minWidth: 0,
                            }}
                          >
                            {/* Avatar circle with orange gradient */}
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                background:
                                  "linear-gradient(135deg, rgb(255,144,21), rgba(255,193,0,0.85))",
                                color: "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 700,
                                fontSize: "0.82rem",
                                letterSpacing: "0.02em",
                                flexShrink: 0,
                                boxShadow: SHADOW_AVATAR_INTERNE,
                                transition: TRANSITION_SMOOTH,
                              }}
                            >
                              {(row.prenom?.[0] || "").toUpperCase()}
                              {(row.nom?.[0] || "").toUpperCase()}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "0.9rem",
                                  lineHeight: 1.3,
                                  letterSpacing: "-0.015em",
                                  color: "rgba(0,40,128,0.9)",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.nom} {row.prenom}
                              </Typography>
                              {row.matricule && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.73rem",
                                    fontWeight: 450,
                                    color: "rgba(0,40,128,0.45)",
                                    letterSpacing: "0.01em",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {row.matricule}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {/* Team chip */}
                          {row.team_name && visibleTeams.length <= 1 && (
                            <Chip
                              label={row.team_name}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: "0.68rem",
                                fontWeight: 500,
                                height: 24,
                                maxWidth: 130,
                                borderRadius: "8px",
                                borderColor: "rgba(0,40,128,0.12)",
                                color: "rgba(0,40,128,0.6)",
                                transition: TRANSITION_SMOOTH,
                              }}
                            />
                          )}
                        </Box>

                        {/* Month grid: 4 columns x 3 rows */}
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(6, 1fr)",
                            gap: 0.75,
                            mt: 0.5,
                          }}
                        >
                          {Array.from({ length: 12 }, (_, i) => i + 1).map(
                            (monthNum) => {
                              const monthData = row.data?.[monthNum.toString()] || {};
                              const yesCount = monthData.yes || 0;
                              const noCount = monthData.no || 0;
                              const color = getRatioColor(yesCount, noCount);
                              const isFuture = isFutureMonth(monthNum);

                              // Compute ratio for tooltip-like display
                              const hasData = yesCount + noCount > 0;
                              const ratio = hasData
                                ? ((yesCount / (yesCount + noCount)) * 100).toFixed(0)
                                : null;

                              return (
                                <Box
                                  key={monthNum}
                                  onClick={() => {
                                    if (!isFuture) {
                                      handleMonthTap(row, monthNum);
                                    }
                                  }}
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 0.25,
                                    cursor: isFuture ? "default" : "pointer",
                                    opacity: isFuture ? 0.3 : 1,
                                    transition: TRANSITION_SMOOTH,
                                    borderRadius: "10px",
                                    py: 0.5,
                                    minHeight: 44,
                                    minWidth: 44,
                                    justifyContent: "center",
                                    "&:active": isFuture
                                      ? {}
                                      : {
                                          transform: "scale(0.9)",
                                          bgcolor: "rgba(0,0,0,0.04)",
                                        },
                                  }}
                                >
                                  {/* Colored circle */}
                                  <Box
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: "50%",
                                      bgcolor: color,
                                      boxShadow:
                                        color === "#70AD47"
                                          ? "0 2px 6px rgba(112,173,71,0.4)"
                                          : color === "#FF0000"
                                          ? "0 2px 6px rgba(255,0,0,0.3)"
                                          : color === "#ED7D31"
                                          ? "0 2px 6px rgba(237,125,49,0.3)"
                                          : color === "#FFFF00"
                                          ? "0 2px 6px rgba(200,200,0,0.25)"
                                          : "0 1px 3px rgba(0,0,0,0.1)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      transition: TRANSITION_SMOOTH,
                                    }}
                                  >
                                    {/* Show percentage inside circle if data exists */}
                                    {hasData && (
                                      <Typography
                                        sx={{
                                          fontSize: "0.55rem",
                                          fontWeight: 700,
                                          color:
                                            color === "#FFFF00"
                                              ? "rgba(0,0,0,0.7)"
                                              : color === "#bcbcbc"
                                              ? "rgba(0,0,0,0.4)"
                                              : "rgba(255,255,255,0.95)",
                                          lineHeight: 1,
                                          letterSpacing: "-0.02em",
                                        }}
                                      >
                                        {ratio}%
                                      </Typography>
                                    )}
                                  </Box>

                                  {/* Month abbreviation */}
                                  <Typography
                                    sx={{
                                      fontSize: "0.6rem",
                                      fontWeight: 500,
                                      color: "rgba(0,40,128,0.5)",
                                      lineHeight: 1,
                                      letterSpacing: "0.01em",
                                      textTransform: "capitalize",
                                    }}
                                  >
                                    {MONTH_ABBR[monthNum - 1]}
                                  </Typography>
                                </Box>
                              );
                            }
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Fade>
                ))}

                {/* Team total */}
                {group.rows.length > 0 && visibleTeams.length > 1 && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textAlign: "right",
                      pr: 1,
                      mb: 0.5,
                      fontWeight: 500,
                      fontSize: "0.72rem",
                      color: "rgba(0,40,128,0.45)",
                      letterSpacing: "0.01em",
                    }}
                  >
                    Total : {group.rows.length}
                  </Typography>
                )}
              </Collapse>
            </Box>
          );
        })}

        {totalCount === 0 && (
          <Fade in timeout={500}>
            <Box
              sx={{
                textAlign: "center",
                py: 10,
                opacity: 0.7,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 450,
                  color: "rgba(0,40,128,0.35)",
                  letterSpacing: "0.02em",
                }}
              >
                <Langs str="No results" />
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>
    </Box>
  );
}
